// frontend/src/pages/dashboard/TaskPage.js
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Chip, 
  Typography, 
  CircularProgress,
  Pagination,
  TablePagination
} from "@mui/material";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { taskAPI, commentAPI, attachmentAPI } from "../../services/api";
import axios from "axios";
import { pollingManager } from '../../hooks/RealtimePoll';
// Custom Hooks
import { useTaskData } from "./Taskhooks/useTaskData";
import { useTaskRealtime } from "./Taskhooks/useTaskRealtime";
import { useTaskTimer } from "./Taskhooks/useTaskTimer";
import { useTaskFilters } from "./Taskhooks/useTaskFilters";
import { useTaskSort } from "./Taskhooks/useTaskSort";
// Components
import TaskHeader from "./TaskComponents/TaskHeader";
import TaskFilterBar from "./TaskComponents/TaskFilterBar";
import TaskCard from "./TaskComponents/TaskCard";
import TaskTable from "./TaskComponents/TaskTable";
import TaskCardSkeleton from "./TaskComponents/TaskCardSkeleton";
import TaskTableRowSkeleton from "./TaskComponents/TaskTableRowSkeleton";
import TaskFormDialog from "./TaskComponents/TaskFormDialog";
import TaskDetailsDialog from "./TaskComponents/TaskDetailsDialog";
import TaskAttachmentDialog from "./TaskComponents/TaskAttachmentDialog";
import ImagePreviewDialog from "./TaskComponents/ImagePreviewDialog";
// Utils
import { highlightMentions } from "../../utils/sanitizeContent";
import { convertLocalToUTCStrict } from "../../utils/taskHelpers";

const TaskPage = () => {
  const { user: authUser, company } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project");
  const searchInputRef = useRef(null);

  // State
  const [selectedProjectId, setSelectedProjectId] = useState(projectId);
  const [isSaving, setIsSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("add");
  const [canEditTask, setCanEditTask] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    project_id: "",
    assigned_user_ids: [],
    due_date: "",
    priority: "medium",
    status_id: "",
    sprint_id: "",
    story_points: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState([]);
  const [taskAttachmentDialogOpen, setTaskAttachmentDialogOpen] = useState(false);
  const [selectedTaskAttachments, setSelectedTaskAttachments] = useState([]);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [taskComments, setTaskComments] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [newCommentText, setNewCommentText] = useState({});
  const [commentFiles, setCommentFiles] = useState({});
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [lockedFields, setLockedFields] = useState({ project_id: false, assigned_user_ids: false, sprint_id: false });
  const [selectedImage, setSelectedImage] = useState({
    url: "",
    name: "",
    attachment: null,
  });
  const [taskHistory, setTaskHistory] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});

  // Custom Hooks
  const hasPermission = useCallback(
    (perm) => {
      if (!authUser) return false;
      if (authUser.hasPermission && typeof authUser.hasPermission === "function")
        return authUser.hasPermission(perm);
      const role = authUser.teamMembership?.role;
      if (!role) return false;
      if (role.name?.toLowerCase() === "system administrator") return true;
      const perms = Array.isArray(role.permissions)
        ? role.permissions.map((p) => p.name)
        : [];
      return perms.includes(perm);
    },
    [authUser]
  );

  const {
    tasks,
    setTasks,
    projects,
    users,
    taskStatuses,
    loading,
    error,
    pagination,
    handlePageChange,
    loadTaskComments,
    fetchAttachments,
  } = useTaskData(authUser, company, searchParams, hasPermission);

  const {
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    searchOpen,
    setSearchOpen,
    filteredTasks,
    resetFilters,
    renderFilterChips,
    removeFilter,
  } = useTaskFilters(tasks, authUser, hasPermission, taskStatuses, users, projects, sprints);

  const {
    sortConfig,
    handleSort,
    sortedTasks,
    clearSort
  } = useTaskSort(filteredTasks);

  const {
    isTracking,
    currentTimeEntryId,
    timeEntryOwner,
    ownerName,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    checkActiveTimer,
    setIsTracking,
    setCurrentTimeEntryId,
  } = useTaskTimer(authUser, setTasks);

  useTaskRealtime(authUser, hasPermission, expandedTaskId, setTasks, setTaskComments);

  // Priority Options
  const priorityOptions = useMemo(() => {
    const uniquePriorities = new Set(tasks.map((t) => t.priority).filter(Boolean));
    return Array.from(uniquePriorities).map((p) => ({
      value: p,
      label: p.charAt(0).toUpperCase() + p.slice(1),
    }));
  }, [tasks]);

  // Fetch Sprints
  const fetchSprints = async (projectId) => {
    if (!projectId) {
      setSprints([]);
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/${projectId}/sprints`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSprints(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load sprints:', err);
      setSprints([]);
    }
  };

  const fetchedProjectsRef = useRef(new Set());

  // Fetch sprints for all projects in the task list
  useEffect(() => {
  const fetchSprintsForProjects = async () => {
    if (!tasks.length) return;
    
    const uniqueProjectIds = [...new Set(
      tasks
        .map(t => t.project_id)
        .filter(Boolean)
    )];
    
    if (uniqueProjectIds.length === 0) return;
    
    // Filter only projects we haven't fetched yet
    const projectsToFetch = uniqueProjectIds.filter(
      id => !fetchedProjectsRef.current.has(id)
    );
    
    if (projectsToFetch.length === 0) return; // Nothing new to fetch
    
    try {
      const token = localStorage.getItem('access_token');
      const allSprints = [];
      
      for (const projectId of projectsToFetch) {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/projects/${projectId}/sprints`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (Array.isArray(res.data)) {
          allSprints.push(...res.data);
        }
        // Mark as fetched
        fetchedProjectsRef.current.add(projectId);
      }
      
      // Merge with existing sprints, avoid duplicates
      setSprints(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const newSprints = allSprints.filter(s => !existingIds.has(s.id));
        return [...prev, ...newSprints];
      });
    } catch (err) {
      console.error('Failed to load sprints:', err);
    }
  };
  
  fetchSprintsForProjects();
}, [tasks]);

  useEffect(() => {
    if (formData.project_id) {
      fetchSprints(formData.project_id);
    }
  }, [formData.project_id]);

  // Comment Handlers
  const handlePostComment = async (taskId) => {
    let content = (newCommentText[taskId] || "").trim();
    if (!content) return;
    content = content.replace(/@\[([^\]]+)\]\([^)]+\)/g, "@$1");
    const fd = new FormData();
    fd.append("content", content);
    const files = commentFiles[taskId];
    if (files) Array.from(files).forEach((file) => fd.append("files[]", file));
    const tempId = `temp-${Date.now()}`;
    const tempComment = {
      id: tempId,
      content,
      user_id: authUser?.id,
      user: authUser,
      attachments: [],
      created_at: new Date().toISOString(),
      is_local: true,
    };
    setTaskComments((prev) => ({
      ...prev,
      [taskId]: [tempComment, ...(prev[taskId] || [])],
    }));
    setNewCommentText((prev) => ({ ...prev, [taskId]: "" }));
    setCommentFiles((prev) => ({ ...prev, [taskId]: null }));
    try {
      const res = await commentAPI.create(taskId, fd);
      const newComment = res.data;
      setTaskComments((prev) => {
        const list = prev[taskId] || [];
        const updated = list.map((c) => (c.id === tempId ? newComment : c));
        return { ...prev, [taskId]: updated };
      });
    } catch (err) {
      console.error("Failed to post comment:", err);
      setTaskComments((prev) => ({
        ...prev,
        [taskId]: (prev[taskId] || []).filter((c) => c.id !== tempId),
      }));
      toast.error(err.response?.data?.error || "Failed to post comment");
    }
  };

  const fetchTaskHistory = async (taskId) => {
    setHistoryLoading(prev => ({ ...prev, [taskId]: true }));
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/tasks/${taskId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTaskHistory(prev => ({ ...prev, [taskId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const handleDeleteComment = async (taskId, commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await commentAPI.delete(commentId);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, comment_count: Math.max(0, (t.comment_count || 0) - 1) }
            : t
        )
      );
      setTaskComments((prev) => ({
        ...prev,
        [taskId]: (prev[taskId] || []).filter((c) => c.id !== commentId),
      }));
    } catch (err) {
      toast.error("Failed to delete comment");
    }
  };

  // Download Handler
  const handleDownload = async (attachment) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`,
        { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed: Please re-login.");
    }
  };

  // Image Preview Handler
  const handleImagePreview = async (attachment) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const imageUrl = window.URL.createObjectURL(new Blob([response.data]));
      setSelectedImage({
        url: imageUrl,
        name: attachment.original_name,
        attachment: attachment,
      });
      setImagePreviewOpen(true);
    } catch (error) {
      console.error('Failed to load image preview:', error);
      toast.error('Failed to load image preview');
    }
  };

  const handlePreviewDownload = async () => {
    if (selectedImage.attachment) {
      await handleDownload(selectedImage.attachment);
    }
  };

  // Dialog Handlers
  const handleOpenAddDialog = () => {
    const projectIdFromUrl = searchParams.get("project");
    if (projectIdFromUrl && projects.length === 0) {
      toast.info("Please wait, loading projects...");
      return;
    }
    let assignedUserIds = [authUser.id];
    let lockedFieldsData = { project_id: false, assigned_user_ids: false, sprint_id: false };
    if (projectIdFromUrl) {
      const project = projects.find((p) => String(p.id) === String(projectIdFromUrl));
      if (project) {
        assignedUserIds = [authUser.id];
        if (project?.assignedUsers?.length) {
          const projectAssignedIds = project.assignedUsers.map((u) => u.id);
          assignedUserIds = [...new Set([...assignedUserIds, ...projectAssignedIds])];
        }
        lockedFieldsData = {
          project_id: true,
          assigned_user_ids: true,
          sprint_id: false,
        };
        setFormData({
          title: "",
          description: "",
          project_id: String(project.id),
          assigned_user_ids: assignedUserIds,
          due_date: "",
          priority: "medium",
          status_id: "",
          sprint_id: "",
          story_points: "",
        });
      }
    } else {
      setFormData({
        title: "",
        description: "",
        project_id: "",
        assigned_user_ids: [authUser.id],
        due_date: "",
        priority: "medium",
        status_id: "",
        sprint_id: "",
        story_points: "",
      });
    }
    setCurrentTask(null);
    setFormErrors({});
    setFiles([]);
    setAttachments([]);
    setLockedFields(lockedFieldsData);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (task) => {
    const isAssigned = task.assignedUsers?.some((u) => u.id === authUser.id);
    const canUseTimer =
      isAssigned ||
      hasPermission("task.update") ||
      hasPermission("task.manage.team");
    if (!canUseTimer) {
      toast.error("You do not have permission to edit this task.");
      return;
    }
    const userCanEdit = hasPermission("task.update") ||
      hasPermission("task.manage.team") ||
      task.created_by === authUser.id || isAssigned;
    setCanEditTask(userCanEdit);
    setDialogType("edit");
    fetchSprints(task.project_id);
    setFormData({
      title: task.title,
      description: task.description,
      project_id: task.project_id?.toString() || "",
      assigned_user_ids: task.assignedUsers?.map((u) => u.id) || [],
      due_date: task.due_date
        ? convertLocalToUTCStrict(task.due_date, company?.timezone)
        : "",
      priority: task.priority,
      status_id: task.status_id?.toString() || "",
      sprint_id: task.sprint_id || "",
      story_points: task.story_points || "",
    });
    setCurrentTask(task);
    setFormErrors({});
    setFiles([]);
    setOpenDialog(true);
    fetchAttachments(task.id).then(setAttachments);
    checkActiveTimer(task.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", formData.title);
      fd.append("description", formData.description || "");
      fd.append("project_id", formData.project_id);
      fd.append(
        "due_date",
        formData.due_date
          ? convertLocalToUTCStrict(formData.due_date, company.timezone)
          : ""
      );
      fd.append("priority", formData.priority);
      fd.append("status_id", formData.status_id || "");
      if (formData.sprint_id) fd.append("sprint_id", formData.sprint_id);
      if (formData.story_points) fd.append("story_points", formData.story_points);
      const finalSet = new Set();
      const project = projects.find(
        (p) => String(p.id) === String(formData.project_id)
      );
      if (project?.assignedUsers?.length)
        project.assignedUsers.forEach((u) => finalSet.add(u.id));
      if (Array.isArray(formData.assigned_user_ids))
        formData.assigned_user_ids.forEach((id) => finalSet.add(id));
      if (authUser?.id) finalSet.add(authUser.id);
      Array.from(finalSet).forEach((id) =>
        fd.append("assigned_user_ids[]", id)
      );
      if (files?.length)
        files.forEach((file) => fd.append("attachments[]", file));
      if (dialogType === "add") await taskAPI.create(fd);
      else await taskAPI.update(currentTask.id, fd);
      setOpenDialog(false);
      toast.success(dialogType === "add" ? "Task created" : "Task updated");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403 && err.response?.data?.error)
        toast.error(err.response.data.error);
      else if (err.response?.status === 422 && err.response?.data?.errors)
        setFormErrors(err.response?.data?.errors);
      else toast.error(err.response?.data?.error || "Failed to save task");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (taskId) => {
    if (!hasPermission("task.delete")) {
      toast.error("No permission");
      return;
    }
    if (window.confirm("Delete this task?")) {
      taskAPI.delete(taskId).then(() => {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        toast.error("Task deleted");
      });
    }
  };

  const handleViewProject = (projectId) =>
    navigate(`/projects/${projectId}/board`);

  const handleViewDetails = (task) => {
    setViewingTask(task);
    fetchAttachments(task.id).then(setAttachments);
    setViewDetailsOpen(true);
  };

  const handleCloseViewDetails = () => {
    setViewDetailsOpen(false);
    setViewingTask(null);
    setAttachments([]);
  };

  const onViewAttachments = (attachments) => {
    setSelectedTaskAttachments(attachments);
    setTaskAttachmentDialogOpen(true);
  };

  const onToggleComments = (id) => {
    if (expandedTaskId === id && expandedSection === 'comments') {
      setExpandedTaskId(null);
      setExpandedSection(null);
    } else {
      setExpandedTaskId(id);
      setExpandedSection('comments');
      setCommentLoading((prev) => ({ ...prev, [id]: true }));
      loadTaskComments(id).then((comments) => {
        setTaskComments((prev) => ({ ...prev, [id]: comments }));
        setCommentLoading((prev) => ({ ...prev, [id]: false }));
      });
    }
  };

  const handleToggleHistory = async (taskId) => {
    if (expandedTaskId === taskId && expandedSection === 'history') {
      setExpandedTaskId(null);
      setExpandedSection(null);
    } else {
      setExpandedTaskId(taskId);
      setExpandedSection('history');
      if (!taskHistory[taskId]) {
        await fetchTaskHistory(taskId);
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId, taskId) => {
    try {
      await attachmentAPI.delete(attachmentId);
      fetchAttachments(taskId).then(setAttachments);
      toast.success("Attachment deleted");
    } catch (err) {
      toast.error("Failed to delete attachment");
    }
  };

  const cardHandlers = useMemo(
    () => ({
      onViewProject: handleViewProject,
      onEdit: handleOpenEditDialog,
      onDelete: handleDelete,
      onViewDetails: handleViewDetails,
      onViewAttachments: onViewAttachments,
      onToggleComments: onToggleComments,
      onToggleHistory: handleToggleHistory,
      onPostComment: handlePostComment,
      onDeleteComment: handleDeleteComment,
    }),
    [
      handleViewProject,
      handleOpenEditDialog,
      handleDelete,
      handleViewDetails,
      onViewAttachments,
      onToggleComments,
      handleToggleHistory,
      handlePostComment,
      handleDeleteComment,
    ]
  );

  const timerHandlers = {
    isTracking,
    currentTimeEntryId,
    timeEntryOwner,
    ownerName,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    handleDownload,
    handleDeleteAttachment,
  };

  useEffect(() => {
    return () => {
      pollingManager.stop("tasks");
    };
  }, []);

  // Sync URL with selected project
  useEffect(() => {
    if (filters.project) {
      navigate(`/tasks?project=${filters.project}`);
      setSelectedProjectId(filters.project);
    } else {
      navigate('/tasks');
      setSelectedProjectId(null);
    }
  }, [filters.project, navigate]);

  // Sync with URL projectId
  useEffect(() => {
    if (projectId) {
      setFilters(prev => ({ ...prev, project: projectId }));
      setSelectedProjectId(projectId);
    }
  }, [projectId]);

  // PAGINATION HANDLER
  const handlePaginationChange = useCallback((event, newPage) => {
    handlePageChange(newPage);
  }, [handlePageChange]);

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, flexGrow: 1 }}>
        <TaskHeader
          filteredTasksCount={filteredTasks.length}
          hasPermission={hasPermission}
          onAddTask={handleOpenAddDialog}
          projectId={selectedProjectId}
        />
        <TaskFilterBar
          filters={filters}
          setFilters={setFilters}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          searchOpen={searchOpen}
          setSearchOpen={setSearchOpen}
          searchInputRef={searchInputRef}
          taskStatuses={taskStatuses}
          priorityOptions={priorityOptions}
          users={users}
          projects={projects}
          sprints={sprints}
          resetFilters={resetFilters}
        />
        {renderFilterChips().length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {renderFilterChips().map((chip, i) => (
              <Chip
                key={i}
                label={chip}
                onDelete={() => removeFilter(chip)}
                size="small"
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {loading ? (
            isMobile ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TaskCardSkeleton key={index} />
              ))
            ) : (
              <TaskTableRowSkeleton />
            )
          ) : filteredTasks.length > 0 ? (
            <>
              {isMobile ? (
                filteredTasks.map((task, index) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    index={index}
                    handlers={cardHandlers}
                    authUser={authUser}
                    hasPermission={hasPermission}
                    theme={theme}
                    company={company}
                    newCommentText={newCommentText}
                    setNewCommentText={setNewCommentText}
                    commentFiles={commentFiles}
                    setCommentFiles={setCommentFiles}
                    taskComments={taskComments}
                    commentLoading={commentLoading}
                    handleDownload={handleDownload}
                    handleImagePreview={handleImagePreview}
                    expandedTaskId={expandedTaskId}
                    expandedSection={expandedSection}
                    sprints={sprints}
                    taskHistory={taskHistory}
                    historyLoading={historyLoading}
                  />
                ))
              ) : (
                <TaskTable
                  tasks={sortedTasks}
                  handlers={cardHandlers}
                  authUser={authUser}
                  hasPermission={hasPermission}
                  theme={theme}
                  company={company}
                  newCommentText={newCommentText}
                  setNewCommentText={setNewCommentText}
                  commentFiles={commentFiles}
                  setCommentFiles={setCommentFiles}
                  taskComments={taskComments}
                  commentLoading={commentLoading}
                  handleDownload={handleDownload}
                  handleImagePreview={handleImagePreview}
                  expandedTaskId={expandedTaskId}
                  expandedSection={expandedSection}
                  sprints={sprints}
                  taskHistory={taskHistory}
                  historyLoading={historyLoading}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              )}
              
              {/* ✅ MATERIAL-UI PAGINATION */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                py: 3,
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Page {pagination.page} of {pagination.lastPage}
                </Typography>
                <Pagination
                  count={pagination.lastPage}
                  page={pagination.page}
                  onChange={handlePaginationChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  disabled={loading}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {pagination.total} total tasks
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="body1" color="text.secondary">
                No tasks found
              </Typography>
            </Box>
          )}
        </Box>
        
        <TaskFormDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          dialogType={dialogType}
          canEditTask={canEditTask}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          projects={projects}
          users={users}
          taskStatuses={taskStatuses}
          sprints={sprints}
          attachments={attachments}
          files={files}
          setFiles={setFiles}
          isSaving={isSaving}
          onSubmit={handleSubmit}
          authUser={authUser}
          hasPermission={hasPermission}
          currentTask={currentTask}
          company={company}
          timerHandlers={timerHandlers}
          fetchAttachments={fetchAttachments}
          lockedFields={lockedFields}
        />
        <TaskDetailsDialog
          open={viewDetailsOpen}
          onClose={handleCloseViewDetails}
          viewingTask={viewingTask}
          attachments={attachments}
          handleDownload={handleDownload}
          company={company}
        />
        <TaskAttachmentDialog
          open={taskAttachmentDialogOpen}
          onClose={() => setTaskAttachmentDialogOpen(false)}
          selectedTaskAttachments={selectedTaskAttachments}
          handleDownload={handleDownload}
        />
        <ImagePreviewDialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
          onDownload={handlePreviewDownload}
        />
      </Box>
    </DashboardLayout>
  );
};

export default TaskPage;